import React from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Box, Dialog, DialogContent, Divider, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { Close, FavoriteBorder } from '@mui/icons-material';
import OnlineAvatar from 'components/play/OnlineAvatar';
import { ROUTINE_LIKERS } from 'api/graphql/play';

/**
 * OTRIX Play — quiénes dieron "me gusta" a una rutina del foro. Se abre al tocar el CONTADOR
 * del corazón (el corazón en sí da/quita el like). Tocar a alguien abre su perfil de atleta.
 */
function RoutineLikersDialog({ routineId = null, open, onClose, onOpenAuthor = () => {} }) {
  const { t } = useTranslation(['play']);

  const { data, loading } = useQuery(ROUTINE_LIKERS, {
    variables: { shared_routine_id: routineId },
    skip: !open || !routineId,
    fetchPolicy: 'cache-and-network'
  });

  const likers = data?.routineLikers || [];

  const renderBody = () => {
    if (loading && likers.length === 0) {
      return (
        <Stack spacing={2} sx={{ py: 1 }}>
          {[0, 1, 2].map((i) => (
            <Stack key={i} direction="row" spacing={1.25} alignItems="center">
              <Skeleton variant="circular" width={40} height={40} />
              <Skeleton variant="text" width="60%" />
            </Stack>
          ))}
        </Stack>
      );
    }

    if (likers.length === 0) {
      return (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: 'center' }}>
          <FavoriteBorder sx={{ fontSize: 34, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('forum.likersEmpty', { ns: 'play' })}
          </Typography>
        </Stack>
      );
    }

    return (
      <Stack spacing={2} divider={<Divider flexItem />} sx={{ py: 1 }}>
        {likers.map((a) => (
          <Stack key={a.id} direction="row" spacing={1.25} alignItems="center">
            <OnlineAvatar name={a.full_name} photo={a.photo} online={a.is_online} size={40} onClick={() => onOpenAuthor(a.id)} />
            <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpenAuthor(a.id)}>
              <Typography variant="body2" fontWeight={700} noWrap>
                {a.full_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {a.is_online
                  ? t('forum.online', { ns: 'play' })
                  : t('forum.routinesLabelCount', { ns: 'play', count: a.routines_count })}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: 'surface.content', backgroundImage: 'none', border: '1px solid', borderColor: 'hairline.main' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Typography variant="h6" fontWeight={800}>
          {t('forum.likersTitle', { ns: 'play' })}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>
      <DialogContent>{renderBody()}</DialogContent>
    </Dialog>
  );
}

RoutineLikersDialog.propTypes = {
  routineId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenAuthor: PropTypes.func
};

export default RoutineLikersDialog;
